import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Server, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type Fleet = {
  organizations_on_trial: number;
  organizations_with_a_cloud_server: number;
  cloud_servers: number;
  max_cloud_servers: number | null;
  headroom: number | null;
  full: boolean;
  updated_at: string;
  updated_by_email: string;
};

function Figure({ icon: Icon, label, value, hint }: {
  icon: typeof Server; label: string; value: string; hint: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /> {label}
        </p>
        <p className="mt-1 text-3xl font-bold tracking-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

export default function FreeTrialsPage() {
  const queryClient = useQueryClient();
  const [ceiling, setCeiling] = React.useState('');

  const fleet = useQuery({
    queryKey: ['trial-budget'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/trial-budget/');
      return res.data as Fleet;
    },
  });

  React.useEffect(() => {
    if (!fleet.data) return;
    setCeiling(fleet.data.max_cloud_servers === null ? '' : String(fleet.data.max_cloud_servers));
  }, [fleet.data]);

  const save = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put('/global-admin/trial-budget/', { max_cloud_servers: ceiling });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trial-budget'] });
      toast.success('Saved');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const data = fleet.data;

  return (
    <div className="space-y-6" data-testid="free-trials-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Free trials</h2>
        <p className="text-muted-foreground">
          What the trials are costing us, and the ceiling that keeps that bill predictable.
        </p>
      </div>

      {fleet.isLoading || !data ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="trial-figures">
            <Figure
              icon={Server}
              label="Cloud servers on free trials"
              value={
                data.max_cloud_servers === null
                  ? String(data.cloud_servers)
                  : `${data.cloud_servers} of ${data.max_cloud_servers}`
              }
              hint={
                data.max_cloud_servers === null
                  ? 'No ceiling is set, so this can grow without limit.'
                  : data.full
                    ? 'Full. New trials can pay for a server or connect one of their own.'
                    : `${data.headroom} more can be provisioned free.`
              }
            />
            <Figure
              icon={Users}
              label="Companies on a free trial"
              value={String(data.organizations_on_trial)}
              hint={`${data.organizations_with_a_cloud_server} of them have a cloud server from us.`}
            />
            <Card>
              <CardContent className="pt-5">
                <p className="text-xs font-medium text-muted-foreground">Status</p>
                <p className="mt-2">
                  <Badge variant={data.full ? 'destructive' : 'default'} data-testid="trial-budget-status">
                    {data.max_cloud_servers === null ? 'no ceiling' : data.full ? 'full' : 'room to spare'}
                  </Badge>
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  A server already running is never touched. The ceiling only stops
                  the next free one being made.
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>The ceiling</CardTitle>
              <CardDescription>
                The most cloud servers that may run on free trials at once. It
                starts at 50; leave it empty for no ceiling. When it is reached a
                company signing up can still add billing details and pay for a
                server, or connect a server of their own, which is free however
                many trials are running.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="trial-ceiling">Cloud servers</Label>
                  <Input
                    id="trial-ceiling"
                    type="number"
                    min="0"
                    className="w-40"
                    placeholder="no ceiling"
                    value={ceiling}
                    onChange={(e) => setCeiling(e.target.value)}
                    data-testid="trial-budget-input"
                  />
                </div>
                <Button
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                  data-testid="save-trial-budget"
                >
                  {save.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
              {data.updated_by_email && (
                <p className="text-xs text-muted-foreground">
                  Last set by {data.updated_by_email} on{' '}
                  {new Date(data.updated_at).toLocaleDateString()}.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
